import IconButton from '@mui/material/IconButton';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';
import { CgSpinner } from 'react-icons/cg';
import { GrDocumentDownload } from 'react-icons/gr';
import { MdOutlineFileDownload } from 'react-icons/md';
import { TiWarning } from 'react-icons/ti';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';

import { turnstileAccessFilterAtom } from '@/state/TurnstileAccessFilter.atom';
import { removeTimeZone } from '@/utils/formatDate';
import {
	userCanClearAllEmployees,
	userCanRunTurnstileRoutine,
} from '@/utils/handleSavePermissions';

import api from '../../services/apiSgft';
import { Button } from '../atoms/Button';
import LightTooltip from '../atoms/LightTooltip';
import ModalConfirmation from './ConfirmationModal';

export const TurnstileAccessHeader = () => {
	const queryClient = useQueryClient();
	const [loadingTurnstile, setLoadingTurnstile] = useState(false);
	const [loadingSuricato, setLoadingSuricato] = useState(false);
	const filteredData = useRecoilValue(turnstileAccessFilterAtom);
	const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
	const [isRoutineConfirmationOpen, setIsRoutineConfirmationOpen] =
		useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	async function downloadTurnstile() {
		setLoadingTurnstile(true);
		try {
			const response = await api.get('/extraction/turnstile-access', {
				params: {
					type: filteredData.type,
					date: removeTimeZone(new Date()),
					management: filteredData.management,
					workstation: filteredData.workstation,
					board: filteredData.board,
					jobTitle: filteredData.jobTitle,
					coordination: filteredData.coordination,
					employee: filteredData.employee,
					employeeStatus: filteredData.employeeStatus,
					training: filteredData.training,
					showAllEmployees: filteredData.showAllEmployees,
				},
				responseType: 'blob',
			});
			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				}),
			);
			const link = document.createElement('a');

			link.href = url;
			link.setAttribute(
				'download',
				`Bloqueios de Catraca - SGFT ${format(
					removeTimeZone(new Date()),
					'dd-MM-yyyy',
					{ locale: ptBR },
				)}.xlsx`,
			);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (e) {
			toast.error('Erro ao extrair bloqueios', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		} finally {
			setLoadingTurnstile(false);
		}
	}

	async function downloadSuricato() {
		setLoadingSuricato(true);
		try {
			const response = await api.get('/extraction/turnstile-access/suricato', {
				responseType: 'blob',
			});
			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				}),
			);
			const link = document.createElement('a');

			link.href = url;
			link.setAttribute(
				'download',
				`Bloqueios do Suricato - SGFT ${format(
					removeTimeZone(new Date()),
					'dd-MM-yyyy',
					{ locale: ptBR },
				)}.xlsx`,
			);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (e) {
			toast.error('Erro ao extrair bloqueios do Suricato', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		} finally {
			setLoadingSuricato(false);
		}
	}

	const handleExtraordinaryRelease = () => {
		setIsConfirmationOpen(true);
	};

	const handleCloseConfirmation = () => {
		setIsConfirmationOpen(false);
	};

	const handleRunRoutine = () => {
		setIsRoutineConfirmationOpen(true);
	};

	const handleCloseRoutineConfirmation = () => {
		setIsRoutineConfirmationOpen(false);
	};

	const handleExtraordinaryReleaseConfirmation = async () => {
		setIsSubmitting(true);
		try {
			await api.post('/turnstile-access/clear-all-employees');
			toast.success('Colaboradores liberados com sucesso', {
				theme: 'colored',
				toastId: 'success',
			});
			handleCloseConfirmation();
			queryClient.invalidateQueries({
				queryKey: ['blocked-turnstile-access'],
			});
			queryClient.invalidateQueries({
				queryKey: ['in-risk-turnstile-access'],
			});
			queryClient.invalidateQueries({
				queryKey: ['exception-turnstile-access'],
			});
			queryClient.invalidateQueries({
				queryKey: ['turnstile-access-filters'],
			});
		} catch (e: any) {
			if (e?.response?.data?.error === 'PENDING_LOG_ALREADY_EXISTS') {
				toast.warning(
					'O botão de emergência já está sendo executado, aguarde um momento e tente novamente',
					{
						theme: 'colored',
						toastId: 'warning',
					},
				);
			} else {
				toast.error('Erro ao liberar todos os colaboradores na catraca', {
					theme: 'colored',
					toastId: 'error',
				});
			}
			throw e;
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleRunRoutineManually = async () => {
		setIsSubmitting(true);
		handleCloseRoutineConfirmation();

		try {
			await api.post('/turnstile-access/routine/manually');
			toast.success(
				'Rotina iniciada! Você receberá um email com o resultado.',
				{
					theme: 'colored',
					toastId: 'success',
				},
			);
		} catch (e: any) {
			if (e?.response?.data?.error === 'PENDING_LOG_ALREADY_EXISTS') {
				toast.warning(
					'O envio dos colaboradores para o Suricato já está sendo executado, aguarde um momento e tente novamente',
					{
						theme: 'colored',
						toastId: 'warning',
					},
				);
			} else {
				toast.error('Erro ao iniciar a rotina', {
					theme: 'colored',
					toastId: 'error',
				});
			}
			throw e;
		} finally {
			setIsSubmitting(false);
		}
	};

	const canClearAllEmployees = userCanClearAllEmployees();
	const canRunTurnstileRoutine = userCanRunTurnstileRoutine();

	return (
		<div className="flex items-center gap-2">
			{canClearAllEmployees && (
				<Button
					onClick={handleExtraordinaryRelease}
					className="h-8 rounded-2xl bg-[#F4503A] normal-case text-white hover:bg-[#E03D2A]"
					disabled={isSubmitting}
				>
					<div className="flex items-center justify-center gap-2">
						<span>Liberação Extraordinária</span>
						<TiWarning size={16} />
					</div>
				</Button>
			)}
			{canRunTurnstileRoutine && (
				<Button
					onClick={handleRunRoutine}
					className="h-8 rounded-2xl bg-primary normal-case text-white hover:bg-[#4F46E5]"
					disabled={isSubmitting}
				>
					<span>Atualizar Bloqueios no Suricato</span>
				</Button>
			)}
			<div className="cursor-pointer">
				<LightTooltip title="Extrair Bloqueios do Suricato">
					<IconButton onClick={downloadSuricato} disabled={loadingSuricato}>
						{loadingSuricato ? (
							<CgSpinner
								size={30}
								color="#c1c1c1"
								className="h-full animate-spin"
							/>
						) : (
							<GrDocumentDownload />
						)}
					</IconButton>
				</LightTooltip>
			</div>
			<div className="cursor-pointer">
				<LightTooltip title="Extrair Bloqueios">
					<IconButton onClick={downloadTurnstile} disabled={loadingTurnstile}>
						{loadingTurnstile ? (
							<CgSpinner
								size={30}
								color="#c1c1c1"
								className="h-full animate-spin"
							/>
						) : (
							<MdOutlineFileDownload />
						)}
					</IconButton>
				</LightTooltip>
			</div>
			{isConfirmationOpen && (
				<ModalConfirmation
					title="Liberação Extraordinária"
					description="Tem certeza que deseja liberar todos os colaboradores na catraca?"
					confirmAction={handleExtraordinaryReleaseConfirmation}
					isOpen={isConfirmationOpen}
					onClose={handleCloseConfirmation}
					discartAction={handleCloseConfirmation}
					descriptionClassName="text-start text-sm font-normal"
					isLoading={isSubmitting}
				/>
			)}
			{isRoutineConfirmationOpen && (
				<ModalConfirmation
					title="Atualizar Bloqueios no Suricato"
					description="Tem certeza que deseja executar a rotina de atualização dos bloqueios no Suricato?"
					confirmAction={handleRunRoutineManually}
					isOpen={isRoutineConfirmationOpen}
					onClose={handleCloseRoutineConfirmation}
					discartAction={handleCloseRoutineConfirmation}
					descriptionClassName="text-start text-sm font-normal"
					isLoading={isSubmitting}
				/>
			)}
		</div>
	);
};
